/**
 * Secure Token Storage Utility
 * 
 * Security Considerations:
 * - localStorage is vulnerable to XSS attacks
 * - Any JavaScript running on the page can access localStorage
 * - sessionStorage is slightly better (cleared on tab close)
 * - httpOnly cookies are most secure but require backend changes
 * 
 * This utility provides a layer of abstraction and can be easily
 * switched to a more secure method later.
 */

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user'
};

/**
 * Store access token
 * Using sessionStorage instead of localStorage for better security
 * (tokens are cleared when tab closes)
 */
export const setAccessToken = (token) => {
  try {
    sessionStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
  } catch (error) {
    console.error('Error storing access token:', error);
    // Fallback to memory if sessionStorage fails
    window.__accessToken = token;
  }
};

/**
 * Get access token
 */
export const getAccessToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) || window.__accessToken || null;
  } catch (error) {
    console.error('Error retrieving access token:', error);
    return window.__accessToken || null;
  }
};

/**
 * Store refresh token
 * Note: Refresh tokens should ideally be stored in httpOnly cookies
 */
export const setRefreshToken = (token) => {
  try {
    sessionStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, token);
  } catch (error) {
    console.error('Error storing refresh token:', error);
    window.__refreshToken = token;
  }
};

/**
 * Get refresh token
 */
export const getRefreshToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN) || window.__refreshToken || null;
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return window.__refreshToken || null;
  }
};

/**
 * Store user data
 */
export const setUser = (userData) => {
  try {
    sessionStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(userData));
  } catch (error) {
    console.error('Error storing user data:', error);
    window.__user = userData;
  }
};

/**
 * Get user data
 */
export const getUser = () => {
  try {
    const userStr = sessionStorage.getItem(TOKEN_KEYS.USER);
    return userStr ? JSON.parse(userStr) : window.__user || null;
  } catch (error) {
    console.error('Error retrieving user data:', error);
    return window.__user || null;
  }
};

/**
 * Clear all tokens and user data
 */
export const clearTokens = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(TOKEN_KEYS.USER);
    // Clear memory fallbacks
    delete window.__accessToken;
    delete window.__refreshToken;
    delete window.__user;
  } catch (error) {
    console.error('Error clearing tokens:', error);
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};
